import { useTranslation } from 'react-i18next';
import { intlLocale } from '@/components/pos/intl-locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useApp, MAX_SESSIONS } from '@/context/AppContext';
import { formatUZS } from '@/lib/format';
import { Trash2, RotateCcw, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export default function DraftsDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { drafts, restoreDraft, deleteDraft, sessions } = useApp();

  const handleRestore = (id: string) => {
    if (sessions.length >= MAX_SESSIONS) {
      toast.error(t('pos_drafts.max_sessions', { max: MAX_SESSIONS }));
      return;
    }
    restoreDraft(id);
    onOpenChange(false);
    toast.success(t('pos_drafts.restored'));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('pos_drafts.title')}</DialogTitle>
        </DialogHeader>

        {drafts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{t('pos_drafts.empty')}</p>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-auto">
            {drafts.map(d => (
              <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{d.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <Clock size={11} />
                    {new Date(d.savedAt).toLocaleString(intlLocale())} · {t('pos_common.count_pcs', { n: d.items.length })} · {formatUZS(d.items.reduce((s, i) => s + i.total, 0))}
                  </p>
                  {d.murabahaCustomer && (
                    <p className="text-[11px] text-primary mt-0.5">{t('pos_drafts.nasiya', { name: d.murabahaCustomer.full_name })}</p>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => handleRestore(d.id)}>
                  <RotateCcw size={13} className="mr-1" /> {t('pos_drafts.restore')}
                </Button>
                <button
                  onClick={() => deleteDraft(d.id)}
                  className="p-2 rounded-md text-destructive hover:bg-destructive/10"
                  aria-label={t('pos_drafts.delete_aria')}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
